import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getConfig } from '@edx/frontend-platform';
import { Spinner, Button } from '@openedx/paragon';
import { RiArrowLeftSLine } from '@remixicon/react';
import { useIntl } from '@edx/frontend-platform/i18n';
import { usePaymentGateway } from '@blend-ed/blendxplatform/hooks';
import { getFeatureToggle } from '@blend-ed/blendxplatform/utils';

import { useProgramDetail, useIsEnrolledInProgram } from '../../../gql/hooks/useProgramDetail';
import { useCourseReviews } from '../../../gql/hooks/useCourseReviews';
import { useCart } from '../../../gql/hooks/useCart';
import {
  CourseHeader,
  CourseReviews,
  PurchasePanel,
} from '../CourseDetail/components';
import { ProgramTextSection, ProgramCoursesList } from './components';
import { useProductSubscription } from '../subscriptions/useProductSubscription';
import messages from '../CourseDetail/components/messages';

import '../CourseDetail/index.scss';
import './index.scss';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1200&q=60';

const formatDuration = (duration, metric) => {
  if (!duration) return null;
  const pretty = metric
    ? metric.charAt(0).toUpperCase() + metric.slice(1)
    : '';
  return `${duration} ${pretty}`.trim();
};

const ProgramDetail = () => {
  const intl = useIntl();
  const { programId: rawProgramId } = useParams();
  const programId = decodeURIComponent(rawProgramId || '');
  const navigate = useNavigate();

  const { program, loading, error } = useProgramDetail(programId);
  const { reviews, distribution } = useCourseReviews(programId);
  const { isEnrolled } = useIsEnrolledInProgram(programId);
  const {
    programsInCart,
    programsInWishlist,
    setProgramCartState,
    pendingTarget,
    refetch: refetchCart,
  } = useCart();
  const { gateway } = usePaymentGateway();
  const featuresToggled = getFeatureToggle();
  const paymentsEnabled = !featuresToggled.loading && featuresToggled.features_payments === true;
  // Cart on by default; only an explicit `features_cart === false` disables it.
  const cartEnabled = featuresToggled.features_cart !== false;
  const subscriptionsEnabled = !featuresToggled.loading && featuresToggled.features_subscriptions === true;
  const subscription = useProductSubscription({
    productType: 'program', programId: program?.id, enabled: subscriptionsEnabled,
  });

  if (loading) {
    return (
      <div className="course-detail__state">
        <Spinner animation="border" screenReaderText={intl.formatMessage(messages.loadingProgram)} />
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="course-detail__state course-detail__state--error">
        {intl.formatMessage(messages.programLoadError)}
      </div>
    );
  }

  const imageUrl = program.image
    ? (program.image.startsWith('http')
      ? program.image
      : `${getConfig().LMS_BASE_URL}${program.image}`)
    : FALLBACK_IMAGE;
  const duration = formatDuration(program.duration, program.durationMetric);
  const isInCart = programsInCart.has(program.id);
  const isInWishlist = programsInWishlist.has(program.id);

  const handleToggleCart = () => setProgramCartState(program.id, 'cart');
  const handleToggleWishlist = () => setProgramCartState(program.id, 'wishlist');
  const handleEnrollNow = async () => {
    if (!isInCart) {
      await setProgramCartState(program.id, 'cart');
    }
    const { data: fresh } = await refetchCart();
    const row = (fresh?.cart || []).find(
      (r) => r.programId === program.id
        && (r.status === 'draft' || r.status === 'pending'),
    );
    if (!row || !gateway) {
      return;
    }
    const lmsBase = (getConfig().LMS_BASE_URL || '').replace(/\/$/, '');
    window.location.href = `${lmsBase}/blendxapi/payment/${gateway}/create_payment_intent/?cart_ids=${row.id}`;
  };

  return (
    <div className="course-detail">
      <Button
        variant="tertiary"
        size="sm"
        className="course-detail__back"
        iconBefore={RiArrowLeftSLine}
        onClick={() => navigate(-1)}
      >
        {intl.formatMessage(messages.goBack)}
      </Button>

      <div className="course-detail__layout">
        <div className="course-detail__main">
          <CourseHeader
            title={program.name || intl.formatMessage(messages.untitledProgram)}
            duration={duration}
            rating={distribution.total > 0 ? distribution.avg : null}
            reviewCount={distribution.total}
          />

          <ProgramTextSection
            title={intl.formatMessage(messages.programDescriptionTitle)}
            body={program.description}
          />
          <ProgramTextSection
            title={intl.formatMessage(messages.programAboutTitle)}
            body={program.about}
          />

          <ProgramCoursesList courses={program.programCourses} />

          <CourseReviews reviews={reviews} distribution={distribution} />
        </div>

        <PurchasePanel
          contentId={program.id}
          imageUrl={imageUrl}
          title={program.name}
          price={program.price}
          isInCart={isInCart}
          isInWishlist={isInWishlist}
          isEnrolled={isEnrolled}
          onToggleCart={handleToggleCart}
          onToggleWishlist={handleToggleWishlist}
          onEnrollNow={handleEnrollNow}
          paymentsEnabled={paymentsEnabled}
          cartEnabled={cartEnabled}
          pendingTarget={pendingTarget}
          subscriptionPlans={subscription.plans}
          isSubscribed={subscription.isSubscribed}
          subscriptionPending={subscription.redirecting}
          onSubscribe={subscription.subscribe}
          subscriptionOnly={program.subscriptionOnly}
          inviteOnly={program.inviteOnly}
        />
      </div>
    </div>
  );
};

export default ProgramDetail;
